import React, { useState, useRef, useCallback, useEffect } from 'react';
import { RefreshCw, ArrowDown } from 'lucide-react';

const THRESHOLD = 70; // px to pull before refresh triggers
const MAX_PULL = 120;

const PullToRefresh = ({ onRefresh, children, disabled = false }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;
    // Only trigger when page is scrolled to top
    if (window.scrollY > 0) return;
    startY.current = e.touches[0].clientY;
    pulling.current = true;
  }, [disabled, isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!pulling.current || disabled || isRefreshing) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0 && window.scrollY === 0) {
      // Dampened pull (tapers off as user pulls further)
      const damped = Math.min(MAX_PULL, diff * 0.5);
      setPullDistance(damped);
    } else if (diff < 0) {
      pulling.current = false;
      setPullDistance(0);
    }
  }, [disabled, isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;
    if (pullDistance >= THRESHOLD && !disabled && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(THRESHOLD);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, disabled, isRefreshing, onRefresh]);

  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const progress = Math.min(1, pullDistance / THRESHOLD);
  const shouldTrigger = pullDistance >= THRESHOLD;

  return (
    <>
      {/* Indicator — mobile only */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 flex items-start justify-center pointer-events-none z-40"
        style={{
          transform: `translateY(${pullDistance - 40}px)`,
          transition: pulling.current ? 'none' : 'transform 0.25s ease-out',
          opacity: pullDistance > 0 || isRefreshing ? 1 : 0,
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div
          className="mt-2 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center"
          style={{
            transform: `scale(${0.6 + progress * 0.4})`,
          }}
        >
          {isRefreshing ? (
            <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
          ) : shouldTrigger ? (
            <RefreshCw className="w-4 h-4 text-blue-600" />
          ) : (
            <ArrowDown
              className="w-4 h-4 text-slate-500"
              style={{
                transform: `rotate(${progress * 180}deg)`,
                transition: 'transform 0.1s',
              }}
            />
          )}
        </div>
      </div>

      {/* Content — translated while pulling (mobile only via conditional transform) */}
      <div
        style={{
          transform: pullDistance > 0 || isRefreshing ? `translateY(${pullDistance * 0.5}px)` : undefined,
          transition: pulling.current ? 'none' : 'transform 0.25s ease-out',
        }}
      >
        {children}
      </div>
    </>
  );
};

export default PullToRefresh;
